import store from "@/store/index";
import { GET_USER_DATA, GET_PORTAL_MODE } from "@/store/getters/user";
import { routes } from "@/router/routes";

export const EPORTAL_BUILDING_APPLICATION_VIEW = "eportal_building_application_view";
export const EPORTAL_BUILDING_APPLICATION_CREATE = "eportal_building_application_create";
export const EPORTAL_ENFORCEMENT_VIEW = "eportal_enforcement_view";
export const EPORTAL_SCHEME_PLAN_APPLICATION_CREATE = "eportal_scheme_plan_application_create";
export const EPORTAL_SCHEME_PLAN_APPLICATION_VIEW = "eportal_scheme_plan_application_view";

export const PORTAL_MODE_CLIENT = "client";
export const PORTAL_MODE_PROFESSIONAL = "professional";

const ROUTE_PERMISSIONS = {
  createBuildingApp: EPORTAL_BUILDING_APPLICATION_CREATE,
  ViewBuildings: EPORTAL_BUILDING_APPLICATION_VIEW,
  ViewEnforcements: EPORTAL_ENFORCEMENT_VIEW,
  ViewRenovations: EPORTAL_BUILDING_APPLICATION_VIEW,
  ViewDemolitions: EPORTAL_BUILDING_APPLICATION_VIEW,
  ViewDrafts: EPORTAL_BUILDING_APPLICATION_CREATE,
  CreateSchemePlanApplication: EPORTAL_SCHEME_PLAN_APPLICATION_CREATE,
  QueryClientSchemePlanApplications: EPORTAL_SCHEME_PLAN_APPLICATION_VIEW,
};

const PARENT_GROUP_PERMISSIONS = {
  "Building Applications": EPORTAL_BUILDING_APPLICATION_VIEW,
};

const PROFESSIONAL_ONLY_ROUTES = new Set(["ProfessionalDashboard"]);

const SHARED_ROUTES = new Set(["ViewProfile"]);

export function hasPermission(permission) {
  const userData = store.getters[GET_USER_DATA];
  if (!userData || !userData.permissions) return false;
  return userData.permissions.includes(permission);
}

export function getPortalMode() {
  const userData = store.getters[GET_USER_DATA];
  if (!isProfessionalUser(userData)) {
    return PORTAL_MODE_CLIENT;
  }
  return store.getters[GET_PORTAL_MODE] || PORTAL_MODE_CLIENT;
}

export function isProfessionalUser(userData = store.getters[GET_USER_DATA]) {
  return !!userData?.profile?.role_name;
}

export function getLinkPermission(item) {
  if (item.route?.name && ROUTE_PERMISSIONS[item.route.name]) {
    return ROUTE_PERMISSIONS[item.route.name];
  }
  if (PARENT_GROUP_PERMISSIONS[item.text]) {
    return PARENT_GROUP_PERMISSIONS[item.text];
  }
  return null;
}

function getRouteAccessType(routeName) {
  if (!routeName) return "client";
  if (ROUTE_PERMISSIONS[routeName]) return "permission";
  if (PROFESSIONAL_ONLY_ROUTES.has(routeName)) return "professional";
  if (SHARED_ROUTES.has(routeName)) return "shared";
  if (routeName === "AccountDetails") return "client";
  return "client";
}

export function linkRequiresPermission(item) {
  if (item.children) {
    return item.children.some((child) => linkRequiresPermission(child));
  }
  return getLinkPermission(item) !== null;
}

export function shouldShowNavItem(item) {
  const mode = getPortalMode();
  const isPro = isProfessionalUser();
  const routeName = item.route?.name;
  const accessType = routeName ? getRouteAccessType(routeName) : null;
  const permission = getLinkPermission(item);
  const needsPermission = permission !== null;

  if (accessType === "professional") {
    return isPro && mode === PORTAL_MODE_PROFESSIONAL;
  }

  if (accessType === "shared") {
    return true;
  }

  if (accessType === "client" && routeName === "AccountDetails") {
    return !isPro || mode === PORTAL_MODE_CLIENT;
  }

  if (!isPro || mode === PORTAL_MODE_CLIENT) {
    return !needsPermission && accessType !== "professional";
  }

  if (needsPermission) {
    return hasPermission(permission);
  }

  return false;
}

export function shouldShowNavParent(item) {
  if (item.children) {
    return item.children.some((child) => shouldShowNavItem(child));
  }
  return shouldShowNavItem(item);
}

export function isRouteAccessible(routeName) {
  const accessType = getRouteAccessType(routeName);
  const mode = getPortalMode();
  const isPro = isProfessionalUser();

  if (accessType === "shared") {
    return true;
  }

  if (accessType === "professional") {
    return isPro && mode === PORTAL_MODE_PROFESSIONAL;
  }

  const permission = ROUTE_PERMISSIONS[routeName];
  if (permission) {
    if (!isPro || mode === PORTAL_MODE_CLIENT) {
      return false;
    }
    return hasPermission(permission);
  }

  if (!isPro || mode === PORTAL_MODE_CLIENT) {
    return true;
  }

  return false;
}

const PROFESSIONAL_DEFAULT_ROUTE_NAMES = [
  "ProfessionalDashboard",
  "ViewBuildings",
  "createBuildingApp",
  "ViewEnforcements",
  "CreateSchemePlanApplication",
  "QueryClientSchemePlanApplications",
  "ViewDrafts",
];

export function getDefaultRouteForMode() {
  if (getPortalMode() === PORTAL_MODE_PROFESSIONAL) {
    for (const routeName of PROFESSIONAL_DEFAULT_ROUTE_NAMES) {
      if (isRouteAccessible(routeName)) {
        return { name: routeName };
      }
    }
  }
  return routes.userAccountDetails;
}

export function getDisplayRoleName(roleName) {
  if (!roleName) return "";
  const trimmed = roleName.trim();
  const displayNames = {
    "County Architect": "Architect",
    "Structural Engineer": "Structural Engineer",
    "Physical/Town Planner": "Physical Planner",
  };
  return displayNames[trimmed] || trimmed.replace(/^County /, "");
}
